import crypto from "node:crypto"
import type { DTOForgeProjectContextCreateV2, SupernovaApiClient } from "@supernova-studio/client"

import { TargetEnv } from "../../../types/environment.js"
import { getApiClient as getDefaultApiClient } from "../../api-client.js"
import { SetupPrototypeServices } from "../steps/runtime.js"

export function createPrototypeService(input: {
  env: TargetEnv
  error: (message: string) => never
  getApiClient?: (env: TargetEnv) => Promise<SupernovaApiClient>
  openBrowser: (url: string) => Promise<void>
}): SetupPrototypeServices {
  let apiClientPromise: Promise<SupernovaApiClient> | undefined
  const getApiClient = input.getApiClient ?? getDefaultApiClient
  const getAuthenticatedApiClient = () => {
    apiClientPromise ??= getApiClient(input.env)
    return apiClientPromise
  }

  return {
    async ensureSharedContext(serviceInput) {
      const client = await getAuthenticatedApiClient()

      const createInput = {
        designSystemId: serviceInput.designSystemId,
        name: serviceInput.contextName,
        sandboxTemplate: serviceInput.sandboxTemplate,
        workspaceId: serviceInput.workspaceId,
      } satisfies DTOForgeProjectContextCreateV2

      try {
        return await createProjectContext(client, createInput)
      } catch (caughtError) {
        input.error(
          `Failed to create shared context. ${caughtError instanceof Error ? caughtError.message : "Unknown error"}`,
        )
      }
    },
    async createPrototypeProject(serviceInput) {
      const client = await getAuthenticatedApiClient()
      const featureId = crypto.randomUUID()
      const messageId = crypto.randomUUID()
      const { project } = await client.forge.projects.create(serviceInput.workspaceId, {
        description: serviceInput.promptText,
        initialFeature: {
          description: serviceInput.promptText,
          id: featureId,
          initialMessage: {
            attachments: {},
            body: serviceInput.promptText,
            id: messageId,
            isPrompt: true,
            startsNewThread: false,
          },
          name: serviceInput.projectName,
        },
        instruction: null,
        name: serviceInput.projectName,
        projectContextId: serviceInput.contextId,
        tags: [],
      })
      const projectId = project.id
      if (!projectId) {
        input.error("Project creation failed: missing project ID in response.")
      }

      return {
        featureId,
        projectId,
      }
    },
    openBrowser: input.openBrowser,
  }
}

async function createProjectContext(
  client: SupernovaApiClient,
  createInput: Pick<DTOForgeProjectContextCreateV2, "workspaceId" | "designSystemId" | "name" | "sandboxTemplate">,
): Promise<string> {
  const { context } = await client.forge.projectContexts.create(createInput)

  return context.id
}
